import type {InlineAutocompleteProps} from '@github-ui/inline-autocomplete'
import {InlineAutocomplete} from '@github-ui/inline-autocomplete'
import type {
  SelectSuggestionsEvent,
  SuggestionsPlacement,
  TextInputCompatibleChild,
} from '@github-ui/inline-autocomplete/types'
import {useEmojiSuggestions} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import {type CSSProperties, useMemo, useState} from 'react'

import {useEmojisQuery} from './use-emojis-query'

export interface EmojiAutocompleteProps extends Omit<
  InlineAutocompleteProps,
  'triggers' | 'suggestions' | 'onShowSuggestions' | 'onHideSuggestions'
> {
  /**
   * Called when a suggestion is selected.
   *
   * @note This should be used only for performing side effects, not for modifying
   * the inserted text. Do not call `setState` in this handler or the user's cursor
   * position / undo history could be lost.
   */
  onSelectSuggestion?: (event: SelectSuggestionsEvent) => void
  /**
   * If `true`, suggestions will be applied with both `Tab` and `Enter`, instead of just
   * `Enter`. This may be expected behavior for users used to IDEs, but use caution when
   * hijacking browser tabbing capability.
   * @default false
   */
  tabInsertsSuggestions?: boolean
  /**
   * The `AutocompleteTextarea` has a container for positioning the suggestions overlay.
   * This can break some layouts (ie, if the editor must expand with `flex: 1` to fill space)
   * so you can override container styles here. Usually this should not be necessary.
   * `position` may not be overridden.
   */
  style?: Omit<CSSProperties, 'position'>
  // Typing this as such makes it look like a compatible child internally, but it isn't actually
  // enforced externally so we have to resort to a runtime assertion.
  /**
   * An `input` or `textarea` compatible component to extend. A compatible component is any
   * component that forwards a ref and props to an underlying `input` or `textarea` element,
   * including but not limited to `Input`, `TextArea`, `input`, `textarea`, `styled.input`,
   * and `styled.textarea`. If the child is not compatible, a runtime `TypeError` will be
   * thrown.
   */
  children: TextInputCompatibleChild
  /**
   * Control which side of the insertion point the suggestions list appears on by default. This
   * should almost always be `"below"` because it typically provides a better user experience
   * (the most-relevant suggestions will appear closest to the text). However, if the input
   * is always near the bottom of the screen (ie, a chat composition form), it may be better to
   * display the suggestions above the input.
   *
   * In either case, if there is not enough room to display the suggestions in the default direction,
   * the suggestions will appear in the other direction.
   * @default "below"
   */
  suggestionsPlacement?: SuggestionsPlacement
  /**
   * The name of the portal to render the suggestions overlay into. This is useful if you
   * have multiple inline autocomplete components on the same page and you want to ensure
   * the suggestions overlay is rendered in the correct order.
   */
  portalName?: string
  /**
   * If `true`, the input will be rendered as a block element, taking up the full width of
   * its container. This is useful for rendering the input in a `flex` container, for example.
   */
  fullWidth?: boolean
  /** Optional title/name for the suggestions menu.  */
  title?: string
  /**
   * For simple cases, the standard representation of this widget is as a `listbox` tied to a `combobox`. For more
   * advanced cases (such as multistep menus or if items need to perform side effects), the widget can instead be
   * represented as a `menu` tied to a plain `textbox`.
   */
  asMenu?: boolean
  tone?: number
}

const emptyArray: [] = []

export function EmojiAutocomplete({tone, ...props}: EmojiAutocompleteProps) {
  const [filter, setFilter] = useState<string>()
  const emojisQuery = useEmojisQuery(filter !== undefined)

  const {calculateSuggestions, trigger} = useEmojiSuggestions(
    emojisQuery.isPending ? 'loading' : (emojisQuery.data ?? emptyArray),
    {tone},
  )

  const suggestions = useMemo(
    () => (filter !== undefined ? calculateSuggestions(filter) : null),
    [filter, calculateSuggestions],
  )

  return (
    <InlineAutocomplete
      triggers={[trigger]}
      suggestions={suggestions}
      onShowSuggestions={({query}) => setFilter(query)}
      onHideSuggestions={() => setFilter(undefined)}
      {...props}
    />
  )
}

try{ EmojiAutocomplete.displayName ||= 'EmojiAutocomplete' } catch {}